import { useInfiniteQuery } from '@tanstack/react-query';
import { BuildingIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TenantWithOrganization } from 'sdk';
import { appConfig } from 'shared';
import { useSearchParams } from '~/hooks/use-search-params';
import { ContentPlaceholder } from '~/modules/common/content-placeholder';
import type { RowsChangeData } from '~/modules/common/data-grid';
import { DataTable } from '~/modules/common/data-table/data-table';
import { useSortColumns } from '~/modules/common/data-table/sort-columns';
import { tenantsListQueryOptions, useTenantUpdateMutation } from '~/modules/tenants/query';
import type { TenantsRouteSearchParams } from '~/modules/tenants/search-params-schemas';
import { TenantsTableBar } from '~/modules/tenants/table/tenants-bar';
import { useColumns } from '~/modules/tenants/table/tenants-columns';

const LIMIT = appConfig.requestLimits.users; // Use users limit as fallback

function rowKeyGetter(row: TenantWithOrganization) {
  return row.id;
}

function TenantsTable() {
  const { t } = useTranslation();
  const { search, setSearch } = useSearchParams<TenantsRouteSearchParams>({ from: '/_app/system/tenants' });

  const { q, sort, order } = search;
  const limit = LIMIT;

  const [columns, setColumns] = useColumns();
  const { sortColumns, setSortColumns: onSortColumnsChange } = useSortColumns(sort, order, setSearch);
  const updateTenant = useTenantUpdateMutation();

  const queryOptions = tenantsListQueryOptions({ ...search, limit });
  const {
    data: rows,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    ...queryOptions,
    select: ({ pages }) => pages.flatMap(({ items }) => items),
  });

  const fetchMore = async () => {
    if (!hasNextPage || isLoading || isFetching) return;
    await fetchNextPage();
  };

  const onRowsChange = (
    changedRows: TenantWithOrganization[],
    { indexes, column }: RowsChangeData<TenantWithOrganization>,
  ) => {
    if (column.key !== 'status') return;
    for (const index of indexes) {
      const tenant = changedRows[index];
      updateTenant.mutate({ path: { tenantId: tenant.id }, body: { status: tenant.status } });
    }
  };

  return (
    <>
      <TenantsTableBar
        queryKey={queryOptions.queryKey}
        columns={columns}
        setColumns={setColumns}
        searchVars={{ ...search, limit }}
        setSearch={setSearch}
      />
      <DataTable<TenantWithOrganization>
        {...{
          rows,
          rowHeight: 52,
          rowKeyGetter,
          onRowsChange,
          columns,
          enableVirtualization: true,
          limit,
          error,
          isLoading,
          isFetching,
          isFiltered: !!q,
          hasNextPage,
          fetchMore,
          sortColumns,
          onSortColumnsChange,
          NoRowsComponent: (
            <ContentPlaceholder
              icon={BuildingIcon}
              title="c:no_resource_yet"
              titleProps={{ resource: t('c:tenant_other').toLowerCase() }}
            />
          ),
        }}
      />
    </>
  );
}

export { TenantsTable };
